import { Autocomplete, Chip, TextField, Typography } from "@mui/material";
import React, { useState } from "react";

import AddIcon from "@mui/icons-material/Add";

export type Tag = {
  id: number;
  name: string;
  description?: string | null;
};

interface TagSelectProps {
  value: Tag[];
  onChange: (tags: Tag[]) => void;
  availableTags: Tag[];
  setAvailableTags?: (tags: Tag[]) => void;
  placeholder?: string;
  error?: boolean;
  errorMessage?: string;
  helperText?: string;
  disabled?: boolean;
  label?: string;
  allowCreation?: boolean;
  isSmText?: boolean;
  /**
   * Base test id for E2E selectors. Applied to the text input itself (so
   * `getByTestId(testId).fill()` works), with `-option-{tagId}` on each
   * dropdown option, `-option-create` on the "Create ..." option, and
   * `-chip-{tagId}` on each selected tag chip.
   */
  testId?: string;
}

export const TagSelect = ({
  value = [],
  onChange,
  availableTags = [],
  setAvailableTags,
  placeholder = "Select or add tags",
  error = false,
  errorMessage,
  helperText,
  disabled = false,
  label,
  allowCreation = true,
  isSmText = false,
  testId,
}: TagSelectProps) => {
  const [inputValue, setInputValue] = useState("");

  const inputExists =
    inputValue.trim() !== "" &&
    availableTags.some(
      (tag) => tag.name.toLowerCase() === inputValue.toLowerCase(),
    );

  const inputSelected =
    inputValue.trim() !== "" &&
    value.some((tag) => tag.name.toLowerCase() === inputValue.toLowerCase());

  const getOptions = () => {
    const filteredOptions = availableTags?.filter(
      (tag) => !value.some((selectedTag) => selectedTag.id === tag.id),
    );

    if (allowCreation && inputValue.trim() && !inputExists && !inputSelected) {
      filteredOptions.push({
        id: -1,
        name: `Create "${inputValue.trim()}"`,
        description: null,
        _isCreateOption: true,
      } as Tag & { _isCreateOption: boolean });
    }

    return filteredOptions;
  };

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: (Tag | string)[],
  ) => {
    const tagObjects = newValue?.filter(
      (item): item is Tag => typeof item !== "string",
    );

    const createOption = tagObjects?.find(
      (tag) => "_isCreateOption" in tag && tag._isCreateOption === true,
    );

    if (createOption) {
      const realTagObjects = tagObjects?.filter(
        (tag) => !("_isCreateOption" in tag),
      );

      const tagName = createOption.name.replace(/^Create "(.+)"$/, "$1");

      const newId = Math.max(0, ...availableTags.map((tag) => tag.id)) + 1;
      const newTag: Tag = {
        id: newId,
        name: tagName,
        description: null,
      };

      if (setAvailableTags) {
        setAvailableTags([...availableTags, newTag]);
      }

      onChange([...realTagObjects, newTag]);
    } else {
      onChange(tagObjects);
    }
  };

  return (
    <Autocomplete
      multiple
      disabled={disabled}
      options={getOptions() || []}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.name
      }
      isOptionEqualToValue={(option, value) => option.id === value.id}
      value={value}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
      onChange={handleChange}
      autoHighlight
      selectOnFocus
      disableCloseOnSelect={false}
      freeSolo={false}
      filterOptions={(options, params) => {
        const filtered = options?.filter(
          (option) =>
            "_isCreateOption" in option ||
            option.name.toLowerCase().includes(params.inputValue.toLowerCase()),
        );
        return filtered;
      }}
      renderOption={(props, option) => {
        if ("_isCreateOption" in option) {
          return (
            <li
              {...props}
              key={option.id}
              data-testid={testId ? `${testId}-option-create` : undefined}
            >
              <AddIcon fontSize="small" sx={{ mr: 1 }} />
              <Typography>{option.name}</Typography>
            </li>
          );
        }

        return (
          <li
            {...props}
            key={option.id}
            data-testid={testId ? `${testId}-option-${option.id}` : undefined}
          >
            {option.name}
          </li>
        );
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            variant="filled"
            label={option.name}
            {...getTagProps({ index })}
            key={option.id}
            data-testid={testId ? `${testId}-chip-${option.id}` : undefined}
            sx={{
              "& .MuiChip-label": {
                mb: 0,
              },
            }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value?.length > 0 ? "" : placeholder}
          variant="outlined"
          size="small"
          error={error}
          helperText={error ? errorMessage : helperText}
          fullWidth
          inputProps={{
            ...params.inputProps,
            ...(testId && { "data-testid": testId }),
          }}
          InputProps={{
            ...params.InputProps,
            ...(isSmText && {
              sx: {
                fontSize: "14px",
              },
            }),
          }}
          InputLabelProps={{
            ...(isSmText && {
              sx: {
                fontSize: "12px",
                pt: 0,
              },
            }),
          }}
        />
      )}
    />
  );
};

export default TagSelect;
